import { Box, Checkbox, FormControlLabel, Grid, IconButton, Switch, Tooltip as MuiTooltip, Typography } from "@material-ui/core";
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAppState } from "../../context/zustand";
import { useAxios } from "../../utils/request";
import { CustomTooltip, getPlatformDisplayName, multiplyObjectsByKey, sumObjectItems, PLATFORMS } from "../../utils/utils";
import CheckboxList from "../CheckboxList";
import KoefPopover from "../KoefPopover";
import { buildGraphCsvFilename, downloadGraphCsv, getCsvDateRange, getEpisodeCsvColumns, getEpisodeMetadataMap } from "../../utils/graphCsv";
import { useEpisodeSelectionConsumer } from "../../hooks/useEpisodeSelectionConsumer";
import { EPISODE_MODE_NONE, EPISODE_MODE_SELECTED, getEpisodeSelectionRequest } from "../../utils/episodeSelection";



const useStyles = makeStyles((theme) => ({
    root: {
        paddingInline: 30
    }
}));


export default function EpisodesTotal(props) {
    const theme = useTheme()
    const classes = useStyles();
    const podcast = useAppState(s => s.podcast?.name);
    const episodeMode = useAppState(s => s.episodeMode);
    const selectedEpisodeIds = useAppState(s => s.selectedEpisodeIds);
    const intervalPreset = useAppState(s => s.intervalPreset);
    const intervalStartWeek = useAppState(s => s.intervalStartWeek);
    const intervalEndWeek = useAppState(s => s.intervalEndWeek);
    const intervalStartDate = useAppState(s => s.intervalStartDate);
    const intervalEndDate = useAppState(s => s.intervalEndDate);
    const episodeMetadata = useAppState(s => s.episodes);
    const setCollectedWeekRange = useAppState(s => s.setCollectedWeekRange);
    const selectionRequest = getEpisodeSelectionRequest(episodeMode, selectedEpisodeIds);
    const selectedEpisodeParam = selectionRequest.episodes;
    const [rawData, setRawData] = useState(false);
    const [episodes, setEpisodes] = useState([]);
    const [platformKoef, setPlatformKoef] = useState({})
    const [isBar, setIsBar] = useState(true);
    const [isLine, setIsLine] = useState(true);
    const [isTop10, setIsTop10] = useState(false);
    const { revision, acknowledgeAfterPaint } = useEpisodeSelectionConsumer('episode-totals');

    useEffect(() => {
        setPlatformKoef(props.coef)
    }, [props.coef])

    const getEpisodesRequest = useAxios(
        ["episodes_total", podcast, episodeMode, selectedEpisodeParam, intervalPreset, intervalStartWeek, intervalEndWeek],
        {
            method: 'get',
            url: `api/allepisodes/`,
            params: {
                episodes: selectedEpisodeParam,
                date_from: intervalStartWeek,
                date_to: intervalEndWeek,
                podcast: podcast
            }
        },
        { enabled: Boolean(podcast) && selectionRequest.enabled },
        () => {},
        () => {}
    )

    useEffect(() => {
        if (episodeMode === EPISODE_MODE_NONE) {
            setRawData(false);
            setEpisodes([]);
            acknowledgeAfterPaint(revision);
            return;
        }

        if (getEpisodesRequest.isFetching) {
            return;
        }

        if (getEpisodesRequest.isError) {
            setRawData(false);
            setEpisodes([]);
            acknowledgeAfterPaint(revision);
            return;
        }

        if (getEpisodesRequest.data) {
            const responseData = getEpisodesRequest.data.data;
            setRawData(responseData);
            setCollectedWeekRange({
                startWeek: responseData.minDateValue,
                endWeek: responseData.maxDateValue,
            });
            setEpisodes(processData(responseData.results));
            acknowledgeAfterPaint(revision);
        }
        // eslint-disable-next-line
    }, [
        acknowledgeAfterPaint,
        episodeMode,
        getEpisodesRequest.data,
        getEpisodesRequest.isError,
        getEpisodesRequest.isFetching,
        revision,
    ]);

    useEffect(() => {
        if (episodeMode !== EPISODE_MODE_NONE && rawData?.results) {
            let data = processData(rawData.results);
            setEpisodes(data)
        }
        // eslint-disable-next-line
    }, [platformKoef, isTop10, episodeMode, selectedEpisodeParam, intervalPreset, intervalStartWeek, intervalEndWeek, episodeMetadata])

    const processData = (resData) => {

        if (!resData) {
            return []
        }

        const totalsByEpisode = Object.values(resData).reduce((acc, weekData) => {
            Object.entries(weekData).forEach(([episode, platforms]) => {
                if (!acc[episode]) {
                    acc[episode] = {};
                }

                Object.entries(platforms).forEach(([platform, quantity]) => {
                    acc[episode][platform] = (acc[episode][platform] || 0) + quantity;
                });
            });

            return acc;
        }, {});

        const episodeMetadataMap = getEpisodeMetadataMap(episodeMetadata);

        let episodes = Object.entries(totalsByEpisode)
            .filter(([key]) => episodeMode !== EPISODE_MODE_SELECTED || selectedEpisodeIds.map(String).includes(String(key)))
            .map(([key, item]) => ({ id: key, quantity: item }))
            .filter(item => (sumObjectItems(item.quantity, {}) !== 0))
            .map(item => {
                const metadata = episodeMetadataMap[String(item.id)] || {};

                return {
                    id: item.id,
                    name: metadata.name || '',
                    release_date: metadata.release_date || '',
                    ...multiplyObjectsByKey(item.quantity, platformKoef),
                    total: sumObjectItems(item.quantity, platformKoef),
                };
            })

        if(isTop10){
            episodes = episodes.sort((a, b) => b.total - a.total).slice(0,10)
        }

        return episodes

            
    }

    const handleDownloadCsv = () => {
        const range = getCsvDateRange({
            intervalPreset,
            intervalStartDate,
            intervalEndDate,
            collectedStartWeek: rawData?.minDateValue,
            collectedEndWeek: rawData?.maxDateValue,
            rows: episodes,
            rowDateKey: 'release_date',
        });

        downloadGraphCsv({
            rows: episodes,
            columns: getEpisodeCsvColumns(platformKoef),
            filename: buildGraphCsvFilename({
                podcast,
                startDate: range.startDate,
                endDate: range.endDate,
                graph: 'totals',
            }),
        });
    }

    const isGraphLoading = getEpisodesRequest.isFetching;

    return (
        <>
            <Grid container spacing={3} className={classes.root}>
                <Grid item lg={12}>
                    <Box paddingY={5} display="flex" alignItems="center" justifyContent="space-between" flexDirection="row">

                        <Box display="flex" alignItems="center" justifyContent="space-between" flexDirection="row">
                            <Typography align="left" variant='h4'>Episodes total</Typography>
                            <Box>
                                <FormControlLabel control={
                                    <Switch checked={isTop10}
                                        color="primary"
                                        onChange={
                                            (e) => {
                                                setIsTop10(e.target.checked)
                                            }
                                        } />} label="Top 10" />
                            </Box>
                        </Box>

                        <Box display="flex" alignItems="center" flexDirection="row">
                            <Box paddingRight={2}>
                                <KoefPopover values={platformKoef} handleChange={
                                    (value) => {
                                        setPlatformKoef(value)
                                    }
                                } >

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isBar}
                                                onChange={() => {
                                                    setIsBar(!isBar);
                                                }}
                                                color="primary"
                                            />
                                        }
                                        label="Platform bars"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isLine}
                                                onChange={() => {
                                                    setIsLine(!isLine);
                                                }}
                                                color="primary"
                                            />
                                        }
                                        label="Line"
                                    />

                                </KoefPopover>
                            </Box>
                            <CheckboxList
                                values={platformKoef}
                                defaultValues={props.coef}
                                handleChange={
                                    (value) => {
                                        setPlatformKoef(value);
                                    }
                                }
                            />
                            <MuiTooltip title="Download .csv">
                                <span>
                                    <IconButton
                                        aria-label="Download .csv"
                                        color="primary"
                                        disabled={episodes.length === 0}
                                        onClick={handleDownloadCsv}
                                    >
                                        <GetAppIcon />
                                    </IconButton>
                                </span>
                            </MuiTooltip>

                        </Box>

                    </Box>

                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                    {episodeMode === EPISODE_MODE_NONE ? (
                        <Box height={400} display="flex" alignItems="center" justifyContent="center">
                            <Typography color="textSecondary">Select at least one episode.</Typography>
                        </Box>
                    ) : isGraphLoading ? (
                        <Box height={400} display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart
                                data={episodes}
                                margin={{
                                    top: 0,
                                    right: 0,
                                    left: 0,
                                    bottom: 40,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="id" textAnchor="end" angle={-45} interval={parseInt(episodes.length / 20)} />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                {isBar && <Bar dataKey="blubrry" name={getPlatformDisplayName("blubrry")} stackId="a" fillOpacity={0.7} fill={PLATFORMS.BLUBRRY.color} />}
                                {isBar && <Bar dataKey="spotify" name={getPlatformDisplayName("spotify")} stackId="a" fillOpacity={0.7} fill={PLATFORMS.SPOTIFY.color} />}
                                {isBar && <Bar dataKey="apple" name={getPlatformDisplayName("apple")} stackId="a" fillOpacity={0.7} fill={PLATFORMS.APPLE.color} />}
                                {isLine && <Line type="monotone" dataKey="total" fill={theme.palette.primary.light} stroke={theme.palette.primary.light} strokeWidth={3} />}

                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                </Grid>
            </Grid >

        </>
    )
}
